/* eslint-disable warp-drive/no-legacy-request-patterns */
import EmberObject from '@ember/object';
import { isEmpty } from '@ember/utils';
import type Store from '@linc-technologies/linced-common/services/store';
import { subjectProgressions } from '@linc-technologies/linced-common/utils/progressions';
import type {
  Expectation,
  Level,
  Progression,
  ProgressionSubject,
} from '@linc-technologies/linced-common/utils/typedefs';

// ProgressDialSource is the part of a subject component that
// loadProgressDialData reads. The components are report/print-subject in
// linced-common and subject-summary in linced-app and linced-parents.
export interface ProgressDialSource {
  showProgressDials: boolean;
  subject: ProgressionSubject & { trackable: boolean };
  levels: readonly Level[] | null;
  // A list of records, or a promise of a list when the component queries it.
  expectationRecords: unknown;
  progressionRecords: unknown;
  // Read only for a trackable subject, after the records load.
  displayData: { expectation?: Expectation; progression?: Progression };
  learnerSummary: unknown;
  // TS-STANDIN(service:hero-api): only goals.getLearnerSummary.
  heroApi: {
    goals: {
      getLearnerSummary(
        schoolId: string,
        personId: string,
        subjectId: string,
        asAtTimeMillis: number | null,
      ): Promise<unknown>;
    };
  };
}

// loadProgressDialData gives the progress dial data of a subject for a
// person. This is the subject progressions (for a trackable subject) and the
// learner summary. It gives undefined when there are no dials to show.
export async function loadProgressDialData(
  source: ProgressDialSource,
  schoolId: string,
  personId: string,
  endTime?: number | null,
) {
  if (!source.showProgressDials) {
    return;
  }

  const { subject } = source;
  if (subject.trackable) {
    // The records can be a query. Wait for them before displayData reads them.
    const [expectations] = await Promise.all([
      source.expectationRecords,
      source.progressionRecords,
    ]);
    if (isEmpty(source.levels) || isEmpty(expectations)) {
      // Levels and Expectations are necessary to show the dials.
      return;
    }
  }

  let summary = source.learnerSummary;
  if (isEmpty(summary)) {
    summary = await source.heroApi.goals.getLearnerSummary(
      schoolId,
      personId,
      subject.id,
      endTime ? endTime * 1000 : null,
    );
  }

  let subjectData;
  if (subject.trackable) {
    const { expectation, progression } = source.displayData;
    subjectData = subjectProgressions(
      subject,
      source.levels ?? [],
      expectation,
      progression,
    );
  } else {
    subjectData = EmberObject.create({});
  }
  subjectData?.set('summary', summary);
  return subjectData;
}

// ProgressionRecordsQuery names the subject, learner and time of the
// expectation and progression records.
export interface ProgressionRecordsQuery {
  goalSubjectId: string;
  personId: string;
  schoolId?: string;
  asAtTime?: number | null;
}

// queryProgressionRecords queries the expectations and progressions of a
// subject for a learner. Set expectations or progressions to false when the
// caller has the records. A store query gives a plain Promise, thus a
// component performs this in a task and reads the records from the task.
export async function queryProgressionRecords(
  store: Pick<Store, 'query'>,
  { goalSubjectId, personId, schoolId, asAtTime }: ProgressionRecordsQuery,
  { expectations = true, progressions = true } = {},
) {
  const subject = schoolId ? { goalSubjectId, schoolId } : { goalSubjectId };
  const [expectationRecords, progressionRecords] = await Promise.all([
    expectations ? store.query('milestones/expectation', subject) : undefined,
    progressions
      ? store.query('milestones/progression', {
          ...subject,
          personId,
          latest: true,
          ...(asAtTime ? { asAtTime } : undefined),
        })
      : undefined,
  ]);
  return {
    expectations: expectationRecords as readonly Expectation[] | undefined,
    progressions: progressionRecords as readonly Progression[] | undefined,
  };
}
